import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Student } from '../../../data/types';
import Button from '../../ui/Button';
import Avatar from '../../ui/Avatar';
import Badge from '../../ui/Badge';
import StudentLayout from '../../layouts/StudentLayout';
import { AuthContext } from '../../../App';

// Define leaderboard view types
type LeaderboardView = 'class' | 'grade' | 'school';
type TimeFrame = 'week' | 'month' | 'allTime';

const Leaderboard: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole } = React.useContext(AuthContext);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<LeaderboardView>('class');
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('week');
  const [currentStudent, setCurrentStudent] = useState<Student | null>(null);
  const [currentRank, setCurrentRank] = useState<number | null>(null);
  
  // Set student session flag when component mounts
  useEffect(() => {
    // Always set the student session flag to true when accessing this page
    localStorage.setItem('isStudentSession', 'true');
    
    // Only redirect if explicitly authenticated as a non-student role
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
  }, [navigate, activeRole]);
  
  // Ensure the student session flag is set
  useEffect(() => {
    if (!localStorage.getItem('isStudentSession')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, []);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get all students
        const allStudents = await dataService.getStudents();
        
        // Sort students by total points in descending order
        const sortedStudents = [...allStudents].sort((a, b) => b.totalPoints - a.totalPoints);
        
        setStudents(sortedStudents);
        
        // For demo purposes, we'll set the current student as the first one
        // In a real app, this would come from auth context
        setCurrentStudent(sortedStudents[0]);
        
        // Find the rank of the current student
        const rank = sortedStudents.findIndex(student => student.id === sortedStudents[0].id) + 1;
        setCurrentRank(rank);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching leaderboard data:', error);
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter students based on current view and time frame
  const getFilteredStudents = () => {
    // In a real app, we would filter based on the current view and time frame
    // For now, we'll just return all students
    return students;
  };
  
  const filteredStudents = getFilteredStudents();
  
  // Get top 10 students
  const topStudents = filteredStudents.slice(0, 10);
  
  // Get nearby students (2 above and 2 below the current student)
  const getNearbyStudents = () => {
    if (!currentRank) return [];
    
    const startIndex = Math.max(0, currentRank - 3);
    const endIndex = Math.min(filteredStudents.length, currentRank + 2);
    
    return filteredStudents.slice(startIndex, endIndex);
  };
  
  const nearbyStudents = getNearbyStudents();
  
  if (loading) {
    return (
      <StudentLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-bounce text-4xl text-purple-600">
            Loading leaderboard...
          </div>
        </div>
      </StudentLayout>
    );
  }
  
  return (
    <StudentLayout>
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500 mb-4">
            Math Champions Leaderboard
          </h1>
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* View Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">View</label>
              <div className="flex space-x-2">
                <Button 
                  color={currentView === 'class' ? 'purple' : 'gray'} 
                  onClick={() => setCurrentView('class')}
                  className="rounded-lg"
                >
                  My Class
                </Button>
                <Button 
                  color={currentView === 'grade' ? 'purple' : 'gray'} 
                  onClick={() => setCurrentView('grade')}
                  className="rounded-lg"
                >
                  My Grade
                </Button>
                <Button 
                  color={currentView === 'school' ? 'purple' : 'gray'} 
                  onClick={() => setCurrentView('school')}
                  className="rounded-lg"
                >
                  My School
                </Button>
              </div>
            </div>
            
            {/* Time Frame Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time Frame</label>
              <div className="flex space-x-2">
                <Button 
                  color={timeFrame === 'week' ? 'blue' : 'gray'} 
                  onClick={() => setTimeFrame('week')}
                  className="rounded-lg"
                >
                  This Week
                </Button>
                <Button 
                  color={timeFrame === 'month' ? 'blue' : 'gray'} 
                  onClick={() => setTimeFrame('month')}
                  className="rounded-lg"
                >
                  This Month
                </Button>
                <Button 
                  color={timeFrame === 'allTime' ? 'blue' : 'gray'} 
                  onClick={() => setTimeFrame('allTime')}
                  className="rounded-lg"
                >
                  All Time
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Top 3 Podium */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Top Champions
          </h2>
          
          <div className="flex flex-col md:flex-row justify-center items-end space-y-4 md:space-y-0 md:space-x-4">
            {/* 2nd Place */}
            {topStudents.length > 1 && (
              <div className="flex flex-col items-center">
                <Avatar 
                  src={topStudents[1].avatarUrl || "https://placehold.co/100x100/6366f1/ffffff?text=2"} 
                  alt={topStudents[1].name} 
                  size="lg" 
                  className="mb-2 border-4 border-gray-300"
                />
                <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-white font-bold mb-2">2</div>
                <div className="text-center">
                  <h3 className="font-bold text-gray-800">{topStudents[1].name}</h3>
                  <p className="text-sm text-gray-500">Grade {topStudents[1].grade}</p>
                  <p className="font-bold text-gray-500">{topStudents[1].totalPoints} pts</p>
                </div>
                <div className="h-24 w-16 bg-gray-200 rounded-t-lg mt-2"></div>
              </div>
            )}
            
            {/* 1st Place */}
            {topStudents.length > 0 && (
              <div className="flex flex-col items-center">
                <div className="relative">
                  <Avatar 
                    src={topStudents[0].avatarUrl || "https://placehold.co/100x100/6366f1/ffffff?text=1"} 
                    alt={topStudents[0].name} 
                    size="xl" 
                    className="mb-2 border-4 border-yellow-400"
                  />
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 .587l3.668 7.568 8.332 1.151-6.064 5.828 1.48 8.279-7.416-3.967-7.417 3.967 1.481-8.279-6.064-5.828 8.332-1.151z"/>
                    </svg>
                  </div>
                </div>
                <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center text-white font-bold mb-2">1</div>
                <div className="text-center">
                  <h3 className="font-bold text-gray-800">{topStudents[0].name}</h3>
                  <p className="text-sm text-gray-500">Grade {topStudents[0].grade}</p>
                  <p className="font-bold text-yellow-500">{topStudents[0].totalPoints} pts</p>
                </div>
                <div className="h-32 w-16 bg-yellow-200 rounded-t-lg mt-2"></div>
              </div>
            )}
            
            {/* 3rd Place */}
            {topStudents.length > 2 && (
              <div className="flex flex-col items-center">
                <Avatar 
                  src={topStudents[2].avatarUrl || "https://placehold.co/100x100/6366f1/ffffff?text=3"} 
                  alt={topStudents[2].name} 
                  size="lg" 
                  className="mb-2 border-4 border-amber-700"
                />
                <div className="w-10 h-10 rounded-full bg-amber-700 flex items-center justify-center text-white font-bold mb-2">3</div>
                <div className="text-center">
                  <h3 className="font-bold text-gray-800">{topStudents[2].name}</h3>
                  <p className="text-sm text-gray-500">Grade {topStudents[2].grade}</p>
                  <p className="font-bold text-amber-700">{topStudents[2].totalPoints} pts</p>
                </div>
                <div className="h-16 w-16 bg-amber-100 rounded-t-lg mt-2"></div>
              </div>
            )}
          </div>
        </div>
        
        {/* Full Leaderboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Top 10 List */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-primary p-4 text-white">
              <h2 className="text-xl font-bold">Top 10 Champions</h2>
              <p className="text-sm opacity-90">
                {currentView === 'class' ? 'In your class' : 
                 currentView === 'grade' ? `In grade ${currentStudent?.grade}` : 
                 'In your school'}
                {timeFrame === 'week' ? ' this week' : 
                 timeFrame === 'month' ? ' this month' : 
                 ' all time'}
              </p>
            </div>
            
            <div className="divide-y divide-gray-100">
              {topStudents.map((student, index) => (
                <div 
                  key={student.id} 
                  className={`flex items-center p-4 ${
                    currentStudent && student.id === currentStudent.id 
                      ? 'bg-purple-50' 
                      : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-3 ${
                    index === 0 ? 'bg-yellow-400' : 
                    index === 1 ? 'bg-gray-300' : 
                    index === 2 ? 'bg-amber-700' : 
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {index + 1}
                  </div>
                  <Avatar 
                    src={student.avatarUrl || `https://placehold.co/100x100/6366f1/ffffff?text=${student.name.charAt(0)}`} 
                    alt={student.name} 
                    size="sm" 
                    className="mr-3" 
                  />
                  <div className="flex-grow">
                    <h4 className="font-bold text-gray-800">
                      {student.name}
                      {currentStudent && student.id === currentStudent.id && (
                        <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          You
                        </span>
                      )}
                    </h4>
                    <p className="text-xs text-gray-500">Grade {student.grade}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-700">{student.totalPoints} pts</div>
                    <div className="text-xs text-gray-500">{student.achievements?.length || 0} achievements</div>
                  </div>
                </div>
              ))}
              
              {topStudents.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  No data available for the selected view and time frame.
                </div>
              )}
            </div>
          </div>
          
          {/* Your Position and Stats */}
          <div className="md:col-span-1">
            {/* Your Position */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
              <div className="bg-primary p-4 text-white">
                <h2 className="text-xl font-bold">Your Position</h2>
              </div>
              
              {currentStudent && currentRank && (
                <div className="p-4">
                  <div className="flex justify-center mb-4">
                    <div className="relative">
                      <Avatar 
                        src={currentStudent.avatarUrl || `https://placehold.co/100x100/6366f1/ffffff?text=${currentStudent.name.charAt(0)}`} 
                        alt={currentStudent.name} 
                        size="xl" 
                        className="border-4 border-purple-200" 
                      />
                      <div className="absolute -bottom-2 -right-2 bg-purple-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-sm font-bold border-2 border-white">
                        {currentRank}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">{currentStudent.name}</h3>
                    <p className="text-gray-600">Grade {currentStudent.grade}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-purple-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-purple-700">{currentStudent.totalPoints}</div>
                      <div className="text-sm text-purple-600">Total Points</div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-blue-700">{currentStudent.achievements?.length || 0}</div>
                      <div className="text-sm text-blue-600">Achievements</div>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Next Rank Progress</span>
                      <span>
                        {currentRank > 1 
                          ? `${currentStudent.totalPoints} / ${students[currentRank - 2]?.totalPoints || 0} pts`
                          : 'You are #1!'}
                      </span>
                    </div>
                    {currentRank > 1 && (
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div 
                          className="bg-gradient-to-r from-green-400 to-blue-500 h-2.5 rounded-full" 
                          style={{ 
                            width: `${Math.min(100, (currentStudent.totalPoints / (students[currentRank - 2]?.totalPoints || 1)) * 100)}%` 
                          }}
                        ></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Nearby students */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Nearby Champions</h4>
                    <div className="space-y-2">
                      {nearbyStudents.map((student, index) => (
                        <div 
                          key={student.id} 
                          className={`flex items-center p-2 rounded-lg ${
                            currentStudent && student.id === currentStudent.id 
                              ? 'bg-purple-50 border border-purple-200' 
                              : 'bg-gray-50'
                          }`}
                        >
                          <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold mr-2">
                            {index + (currentRank > 3 ? currentRank - 2 : 1)}
                          </div>
                          <div className="flex-grow">
                            <h5 className="font-medium text-gray-800 text-sm">
                              {student.name}
                              {currentStudent && student.id === currentStudent.id && (
                                <span className="ml-1 text-xs text-purple-700">
                                  (You)
                                </span>
                              )}
                            </h5>
                          </div>
                          <div className="text-sm font-bold text-gray-700">{student.totalPoints} pts</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Achievement Progress */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="bg-primary p-4 text-white">
                <h2 className="text-xl font-bold">Achievement Progress</h2>
              </div>
              
              <div className="p-4">
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Math Explorer</h4>
                    <Badge variant="success">Completed</Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-green-500 h-2.5 rounded-full w-full"></div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Game Champion</h4>
                    <Badge variant="primary">3/5 Games</Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-blue-500 h-2.5 rounded-full w-3/5"></div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-gray-700">Learning Streak</h4>
                    <Badge variant="warning">7/10 Days</Badge>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-yellow-500 h-2.5 rounded-full w-[70%]"></div>
                  </div>
                </div>
                
                <div>
                  <Button 
                    color="purple" 
                    className="w-full rounded-lg"
                  >
                    View All Achievements
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default Leaderboard;
